// Stimulus controller for the shared pronunciation / typo report modal.
// Used on Lexiqamus search results (AI Latin feedback) and the entry popup (Ottoman typo reports).
import ApplicationController from "./application_controller"

export const FEEDBACK_OPEN_EVENT = "ai-pronunciation-feedback:open"
export const FEEDBACK_ON = {
  ai_pronunciation: "ai_pronunciation",
  ottoman_typo: "ottoman_typo",
}

export default class extends ApplicationController {
  static targets = [
    "modal",
    "feedbackForm",
    "feedbackSuccessView",
    "wordCompareBox",
    "wordCompareOttoman",
    "wordCompareLatin",
    "feedbackMessage",
    "submissionError",
    "submitFeedbackButton",
  ]

  static values = {
    feedbackUrl: String,
    placeholderCorrect: String,
    placeholderIncorrect: String,
    placeholderNeutral: String,
    placeholderTypo: String,
    errorMessage: String,
  }

  connect() {
    this.currentFeedback = null
    this.activePlaceholder = ""

    // Listen for custom events from search results or entry popups
    this.onFeedbackOpenFromDocument = (event) => {
      this.openFromDetail(event.detail || {})
    }
    document.addEventListener(FEEDBACK_OPEN_EVENT, this.onFeedbackOpenFromDocument)
  }

  disconnect() {
    document.removeEventListener(FEEDBACK_OPEN_EVENT, this.onFeedbackOpenFromDocument)
  }

  // Opens the modal and populates it with the feedback context (Ottoman value, Latin value, spelling IDs)
  openFromDetail(detail) {
    const {
      feedbackOn = FEEDBACK_ON.ai_pronunciation,
      feedbackType = "",
      ottomanValue = "",
      latinValue = "",
      spellingId = "",
      entrySpellingId = "",
    } = detail

    if (!spellingId) return
    if (feedbackOn === FEEDBACK_ON.ottoman_typo && !ottomanValue) return
    if (feedbackOn === FEEDBACK_ON.ai_pronunciation && !ottomanValue && !latinValue) return

    const displayLatin = feedbackOn === FEEDBACK_ON.ottoman_typo ? "" : latinValue
    this.currentFeedback = { feedbackOn, feedbackType, latinValue: displayLatin, spellingId, entrySpellingId }
    this.restoreFormView()

    // Populate word comparison box — typo reports show Ottoman only (no AI latin).
    this.wordCompareOttomanTarget.textContent = ottomanValue
    this.wordCompareLatinTarget.textContent = displayLatin
    const typoReport = feedbackOn === FEEDBACK_ON.ottoman_typo
    this.wordCompareBoxTarget.classList.toggle("word-compare--single", typoReport || !ottomanValue || !displayLatin)
    this.wordCompareLatinTarget.classList.toggle("d-none", typoReport)

    // Set appropriate placeholder based on feedback type
    this.activePlaceholder = this.placeholderFor(feedbackOn, feedbackType)
    this.feedbackMessageTarget.value = ""
    this.feedbackMessageTarget.placeholder = this.activePlaceholder

    this.getBootstrapModalInstance()?.show()
  }

  // POST to pronunciation_feedback_lexiqamus_search_results_path
  // Handles both AI pronunciation feedback (correct/incorrect/neutral) and typo reports
  submitFeedback(event) {
    event.preventDefault()

    if (!this.currentFeedback) return

    const message = this.feedbackMessageTarget.value.trim()

    const { feedbackOn, feedbackType, latinValue, spellingId, entrySpellingId } = this.currentFeedback

    if (!spellingId || (feedbackOn === FEEDBACK_ON.ai_pronunciation && !feedbackType)) {
      this.showSubmissionError()
      return
    }

    const submitButton = this.submitFeedbackButtonTarget
    submitButton.disabled = true

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    
    // Build payload matching pronunciation_feedback_params in SearchResultsController
    const payload = {
      ottoman_spelling_id: spellingId,
      feedback_on: feedbackOn,
      message,
    }

    if (entrySpellingId) payload.entry_spelling_id = entrySpellingId
    if (feedbackOn === FEEDBACK_ON.ai_pronunciation) {
      payload.feedback_type = feedbackType
      payload.ai_latin_value = latinValue || ""
    }

    $.ajax({
      url: this.feedbackUrlValue,
      type: "POST",
      dataType: "json",
      data: payload,
      headers: {
        "X-CSRF-Token": csrfToken || "",
      },
    }).done(() => {
      this.showSuccessView()
      if (window.feather) window.feather.replace()
    }).fail(() => {
      this.showSubmissionError()
    }).always(() => {
      submitButton.disabled = false
    })
  }

  // Clear placeholder on focus so user sees empty textarea
  clearFeedbackPlaceholder() {
    this.feedbackMessageTarget.placeholder = ""
  }

  // Restore placeholder on blur if textarea is empty
  restoreFeedbackPlaceholder() {
    if (!this.feedbackMessageTarget.value) {
      this.feedbackMessageTarget.placeholder = this.activePlaceholder
    }
  }

  // Reset form when modal is closed
  onModalHidden() {
    this.restoreFormView()
    this.currentFeedback = null
  }

  // Show success view (checkmark icon + success message)
  showSuccessView() {
    this.feedbackFormTarget.style.display = "none"
    this.feedbackSuccessViewTarget.style.display = "flex"
  }

  // Restore initial form view (hide success, show form, clear inputs, hide errors)
  restoreFormView() {
    this.feedbackFormTarget.style.display = "block"
    this.feedbackSuccessViewTarget.style.display = "none"
    this.feedbackMessageTarget.value = ""
    this.submitFeedbackButtonTarget.disabled = false
    this.submissionErrorTarget.classList.add("d-none")
  }

  // Display submission error message
  showSubmissionError() {
    this.submissionErrorTarget.textContent = this.errorMessageValue
    this.submissionErrorTarget.classList.remove("d-none")
  }

  // Get or create Bootstrap modal instance
  getBootstrapModalInstance() {
    if (!window.bootstrap?.Modal) return null
    return window.bootstrap.Modal.getOrCreateInstance(this.modalTarget)
  }

  // Returns the appropriate placeholder text based on feedback type
  placeholderFor(feedbackOn, feedbackType) {
    if (feedbackOn === FEEDBACK_ON.ottoman_typo) return this.placeholderTypoValue

    switch (feedbackType) {
      case "correct":
        return this.placeholderCorrectValue
      case "incorrect":
        return this.placeholderIncorrectValue
      case "neutral":
        return this.placeholderNeutralValue
      default:
        return ""
    }
  }
}
